import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import RecipeCard from "@/components/RecipeCard";
import Seo from "@/components/Seo";
import { slugify } from "@/lib/slugify";
import { getCountryEditorial } from "@/lib/countryEditorial";
import { ArrowLeft, ArrowRight, Check, Globe } from "lucide-react";

export default function CountryPage() {
  const { countrySlug } = useParams();
  const [origins, setOrigins] = useState({ countries: [], regions: [] });
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Resolve slug -> country name
  const country = useMemo(() => {
    const match = origins.countries.find((c) => slugify(c.name) === countrySlug);
    return match?.name || null;
  }, [origins, countrySlug]);

  const editorial = useMemo(() => (country ? getCountryEditorial(country) : null), [country]);

  // Fetch origins
  useEffect(() => {
    api.get("/recipes/origins").then((r) => setOrigins(r.data));
  }, []);

  // Once country resolved, fetch recipes
  useEffect(() => {
    if (!origins.countries.length) return;
    if (!country) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    api
      .get(`/recipes?country=${encodeURIComponent(country)}&limit=48`)
      .then((r) => setRecipes(r.data.items || []))
      .finally(() => setLoading(false));
  }, [country, origins.countries.length]);

  if (notFound) {
    return (
      <div className="container-x py-20 text-center" data-testid="country-not-found">
        <p className="font-serif text-3xl">Pays introuvable</p>
        <Link to="/cuisines-du-monde" className="mt-4 inline-block text-[hsl(var(--brand-primary))] hover:underline">
          ← Toutes les cuisines du monde
        </Link>
      </div>
    );
  }

  if (!country || !editorial) {
    return <div className="container-x py-20 text-[hsl(var(--text-muted))]">Chargement…</div>;
  }

  // Regions of this country
  const countryRegions = origins.regions.filter((r) => r.country === country);

  const startsWithVowel = /^[AEIOUYaeiouy]/.test(country);
  const dePays = startsWithVowel ? `d'${country}` : `de ${country}`;

  // SEO title always includes "diabète" and "IG bas"
  const pageTitle = `Cuisine ${country} pour diabétiques · Recettes IG bas`;
  const pageDescription = `${recipes.length || "Plusieurs"} recettes traditionnelles ${dePays} adaptées aux personnes diabétiques. Index glycémique bas, sans sucre raffiné, conseils nutrition pour diabète type 1 et type 2.`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: "https://recette-diabete.com/" },
          { "@type": "ListItem", position: 2, name: "Cuisines du monde", item: "https://recette-diabete.com/cuisines-du-monde" },
          { "@type": "ListItem", position: 3, name: `Cuisine ${country}`, item: `https://recette-diabete.com/cuisines-du-monde/${countrySlug}` },
        ],
      },
      {
        "@type": "CollectionPage",
        name: pageTitle,
        description: pageDescription,
        inLanguage: "fr-FR",
        isPartOf: { "@type": "WebSite", name: "Recette-Diabete" },
        about: {
          "@type": "Thing",
          name: `Cuisine ${country.toLowerCase()} pour diabétiques`,
        },
        mainEntity: {
          "@type": "ItemList",
          numberOfItems: recipes.length,
          itemListElement: recipes.slice(0, 20).map((r, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `https://recette-diabete.com/recettes/${r.slug}`,
            name: r.title,
          })),
        },
      },
    ],
  };

  const otherCountries = origins.countries.filter((c) => c.name !== country && c.name !== "France").slice(0, 8);

  return (
    <>
      <Seo
        title={pageTitle}
        description={pageDescription}
        keywords={editorial.seoKeywords}
        path={`/cuisines-du-monde/${countrySlug}`}
        type="article"
        image={recipes?.[0]?.slug ? `/api/recipes/${recipes[0].slug}/image` : undefined}
        jsonLd={jsonLd}
      />

      <article data-testid="country-page">
        {/* HERO */}
        <header className="container-x pt-12 pb-12" data-testid="country-hero">
          <Link to="/cuisines-du-monde" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-[hsl(var(--text-muted))] hover:text-[hsl(var(--brand-primary))]">
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> Cuisines du monde
          </Link>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-8">
              <p className="overline mb-4 flex items-center gap-2">
                <span className="text-base" aria-hidden>{editorial.flag}</span>
                Cuisine {country} · Index glycémique bas
              </p>
              <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl leading-[1.02] tracking-tight" data-testid="country-h1">
                Cuisine {country}<br />
                <em className="text-[hsl(var(--brand-primary))] font-medium">pour diabétiques</em>
              </h1>
              <p className="mt-6 text-lg text-[hsl(var(--text-secondary))] max-w-2xl">
                {editorial.tagline}
              </p>
            </div>
            <div className="md:col-span-4 text-right hidden md:block">
              <p className="font-serif text-7xl text-[hsl(var(--brand-primary))]">{recipes.length}</p>
              <p className="overline">recettes IG bas</p>
            </div>
          </div>
        </header>

        {/* EDITORIAL INTRO */}
        <section className="container-x py-12 border-y border-[hsl(var(--border-light))]">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-7">
              <p className="overline mb-3">À propos de cette cuisine</p>
              <p className="font-serif text-2xl md:text-3xl leading-snug text-[hsl(var(--text-primary))]" data-testid="country-intro">
                {editorial.intro}
              </p>
            </div>
            <div className="md:col-span-5">
              <p className="overline mb-3">Piliers IG bas pour diabétiques</p>
              <ul className="space-y-3" data-testid="country-pillars">
                {editorial.pillars.map((p, i) => (
                  <li key={i} className="flex gap-3">
                    <Check className="w-4 h-4 text-[hsl(var(--brand-primary))] flex-shrink-0 mt-1" strokeWidth={1.5} />
                    <span className="text-[hsl(var(--text-secondary))]">{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* REGIONS (if any) */}
        {countryRegions.length > 0 && (
          <section className="container-x py-8" data-testid="country-regions">
            <div className="flex flex-wrap items-center gap-2">
              <span className="overline mr-2">Régions</span>
              {countryRegions.map((r) => (
                <Link
                  key={r.name}
                  to={`/recettes?country=${encodeURIComponent(country)}&region=${encodeURIComponent(r.name)}`}
                  className="text-xs uppercase tracking-wider px-3 py-1.5 rounded-sm border border-[hsl(var(--border-default))] text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--brand-primary))] hover:border-[hsl(var(--brand-primary))]"
                  data-testid={`country-region-${slugify(r.name)}`}
                >
                  {r.name} ({r.count})
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* RECIPES GRID */}
        <section className="container-x py-12">
          <p className="overline mb-3">{recipes.length} recettes</p>
          <h2 className="font-serif text-4xl mb-10">
            Nos recettes IG bas {dePays}
          </h2>
          {loading ? (
            <div className="text-[hsl(var(--text-muted))] py-12">Chargement…</div>
          ) : recipes.length === 0 ? (
            <div className="border border-dashed border-[hsl(var(--border-default))] rounded-sm p-12 text-center">
              <p className="font-serif text-2xl mb-2">Aucune recette pour {country} pour le moment.</p>
              <Link to="/cuisines-du-monde" className="mt-4 inline-flex items-center gap-1 text-sm text-[hsl(var(--brand-primary))] hover:underline">
                Explorer les autres cuisines <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
              {recipes.map((r) => <RecipeCard key={r.id} recipe={r} />)}
            </div>
          )}
        </section>

        {/* INTERNAL LINKING - OTHER COUNTRIES */}
        {otherCountries.length > 0 && (
          <section className="container-x py-16 border-t border-[hsl(var(--border-light))]" data-testid="country-other-cuisines">
            <p className="overline mb-3">Continuer le voyage</p>
            <h2 className="font-serif text-3xl mb-8">Autres cuisines du monde pour diabétiques</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {otherCountries.map((c) => {
                const ed = getCountryEditorial(c.name);
                return (
                  <Link
                    key={c.name}
                    to={`/cuisines-du-monde/${slugify(c.name)}`}
                    data-testid={`country-link-${slugify(c.name)}`}
                    className="border border-[hsl(var(--border-default))] p-5 hover:border-[hsl(var(--brand-primary))] transition-colors group"
                  >
                    <p className="text-2xl mb-2" aria-hidden>{ed.flag}</p>
                    <p className="font-serif text-xl group-hover:text-[hsl(var(--brand-primary))] transition-colors">{c.name}</p>
                    <p className="text-xs text-[hsl(var(--text-muted))] mt-1">{c.count} recette{c.count > 1 ? "s" : ""}</p>
                  </Link>
                );
              })}
            </div>
            <Link to="/cuisines-du-monde" className="mt-8 inline-flex items-center gap-1 text-sm text-[hsl(var(--brand-primary))] hover:underline">
              <Globe className="w-4 h-4" strokeWidth={1.5} /> Toutes les cuisines du monde
            </Link>
          </section>
        )}
      </article>
    </>
  );
}
